<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import * as Sentry from '@sentry/vue';
import { toast } from 'vue-sonner';
import CloseIcon from '@/assets/icons/shared/close.svg';
import CopyIcon from '@/assets/icons/shared/copy.svg';
import CheckIcon from '@/assets/icons/shared/check.svg';
import { NotificationType } from '@/modules/common/enums/notification-type.ts';
import { useNotificationTimerStore } from '@/modules/common/stores/notification-timer.ts';
import type { NotificationMessage } from '@/modules/common/composables/use-notification.ts';
import CircleCloseIcon from '@/assets/icons/shared/circle/close.svg';
import CircleCheckIcon from '@/assets/icons/shared/circle/check.svg';
import CircleErrorIcon from '@/assets/icons/shared/circle/error.svg';
import CircleInfoIcon from '@/assets/icons/shared/circle/info.svg';

const ICONS = {
  [NotificationType.Error]: CircleCloseIcon,
  [NotificationType.Success]: CircleCheckIcon,
  [NotificationType.Warning]: CircleErrorIcon,
  [NotificationType.Info]: CircleInfoIcon,
};

const TITLES = {
  [NotificationType.Error]: 'common.notification.title.error',
  [NotificationType.Success]: 'common.notification.title.success',
  [NotificationType.Warning]: 'common.notification.title.warning',
  [NotificationType.Info]: 'common.notification.title.info',
};

const DURATION = 5000;
const COPIED_RESET_MS = 2000;

const { id, type, message, detail, fullDetail } = defineProps<{
  id: number;
  type: NotificationType;
  message: NotificationMessage;
  detail?: string;
  fullDetail?: string;
}>();

const timers = useNotificationTimerStore();

const descriptionRef = useTemplateRef<HTMLElement>('description');
const isCopied = ref(false);
let copiedTimeoutId: ReturnType<typeof setTimeout> | undefined;

const remainingTimeMs = ref(DURATION);
let rafRequestId = 0;
let lastFrameTs = 0;
let isHovered = false;

const elapsedPercent = computed(() => {
  const elapsed = DURATION - remainingTimeMs.value;
  return Math.max(0, Math.min(100, (elapsed / DURATION) * 100));
});
const progressWidth = computed(() => `${elapsedPercent.value}%`);

const icon = computed(() => ICONS[type]);
const title = computed(() => TITLES[type]);

const typeClass = computed(() => `_${type}`);
const isCopyable = computed(() => type === NotificationType.Error);

function tick(ts: number) {
  if (!lastFrameTs) {
    lastFrameTs = ts;
  }

  if (!timers.isTimerPaused) {
    const delta = ts - lastFrameTs;
    remainingTimeMs.value = Math.max(0, remainingTimeMs.value - delta);

    if (remainingTimeMs.value <= 0) {
      toast.dismiss(id);
      return;
    }
  }

  lastFrameTs = ts;
  rafRequestId = requestAnimationFrame(tick);
}

function handlePointerClick() {
  isHovered = true;
  timers.hoverEnter();
}
function handlePointerLeave() {
  isHovered = false;
  timers.hoverLeave(true);
}
function handleClose() {
  if (isHovered) {
    isHovered = false;
    timers.hoverLeave(true);
  }
  toast.dismiss(id);
}

async function handleCopy() {
  const text = [descriptionRef.value?.innerText.trim(), fullDetail || detail].filter(Boolean).join('\n');

  try {
    await navigator.clipboard.writeText(text);
    isCopied.value = true;
    clearTimeout(copiedTimeoutId);
    copiedTimeoutId = setTimeout(() => (isCopied.value = false), COPIED_RESET_MS);
  } catch (error) {
    Sentry.captureException(error);
  }
}

onMounted(() => {
  rafRequestId = requestAnimationFrame(tick);
});

onBeforeUnmount(() => {
  if (isHovered) {
    timers.hoverLeave(false);
    isHovered = false;
  }
  cancelAnimationFrame(rafRequestId);
  clearTimeout(copiedTimeoutId);
});
</script>

<template>
  <div
    class="app-toast"
    :class="typeClass"
    @click="handlePointerClick"
    @pointerleave="handlePointerLeave"
  >
    <div class="content">
      <div
        ><component
          :is="icon"
          class="icon"
      /></div>
      <div class="notification">
        <div class="title">{{ $t(title) }}</div>
        <div
          v-if="typeof message !== 'string' && 'component' in message"
          ref="description"
          class="description"
        >
          <component
            :is="message.component"
            v-bind="message.props"
          />
        </div>
        <div
          v-else
          ref="description"
          class="description"
        >
          {{ typeof message === 'string' ? $t(message) : $t(message.key, message.params) }}
        </div>
        <div
          v-if="detail"
          class="detail"
          >{{ detail }}</div
        >
      </div>
      <div class="actions">
        <button
          v-if="isCopyable"
          type="button"
          class="copy"
          :title="$t('common.notification.copy')"
          :aria-label="$t('common.notification.copy')"
          @click="handleCopy"
        >
          <component :is="isCopied ? CheckIcon : CopyIcon" />
        </button>
        <CloseIcon
          class="close"
          @click="handleClose"
        />
      </div>
    </div>
    <div
      class="remaining-bar"
      :class="typeClass"
    />
  </div>
</template>

<style scoped lang="scss">
.app-toast {
  position: relative;
  overflow: hidden;
  padding: 16px;
  max-width: 450px;
  min-width: 280px;
  border-radius: 8px;
  background: var(--color-white);
  border: var(--border-gray-100);
  box-shadow: var(--shadow);

  &._success {
    background: var(--color-green-500);
    border-color: var(--color-green-1000);
  }

  &._warning {
    background: var(--color-yellow-400);
    border-color: var(--color-yellow-1000);
  }

  &._error {
    background: var(--color-red-400);
    border-color: var(--color-red-1000);
  }
}

.content {
  display: flex;
  gap: 8px;
  color: var(--color-black);
  font-size: var(--font-size-l);
  font-weight: var(--font-weight-bold);
}

.notification {
  width: 100%;
}

.icon {
  width: 24px;
  height: 24px;
  fill: var(--color-black);
}

.title {
  margin-bottom: 12px;
  padding-top: 3px;
}

.description {
  margin-bottom: 8px;
}

.detail {
  margin-bottom: 8px;
  padding-top: 8px;
  border-top: var(--border-gray-100);
  word-break: break-word;
}

.actions {
  display: flex;
  gap: 4px;
  align-self: flex-start;
}

.copy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
    fill: var(--color-black);
  }
}

.close {
  width: 24px;
  height: 24px;
  cursor: pointer;
  fill: var(--color-black);
}

.remaining-bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: var(--z-index-notification);
  width: v-bind(progressWidth);
  height: 4px;
  background: var(--color-gray-100);
  transition: width 0.1s linear;

  &._success {
    background: var(--color-green-1000);
  }

  &._warning {
    background: var(--color-yellow-1000);
  }

  &._error {
    background: var(--color-red-1000);
  }
}
</style>
